import type { RecordOfObjectEntity, RecordOfObjectsState, SSRObjectsState } from './types';

import type { BackendEntities } from '../types/backendEntities';
import { transformNodeSSRState, updateField } from '../utils';
import { createObjectEntity, initialState } from './records';
import { transformObject } from './transform';

// ---

export function transformObjectsSSRState(state: SSRObjectsState): RecordOfObjectsState {
  return transformNodeSSRState(state, initialState, (entry) => {
    return createObjectEntity({
      ...entry,
      matchIds: new Set(entry.matchIds),
    });
  });
}


export function createObjectsState(
  state = initialState(),
  { objects }: BackendEntities['diff'],
  objectChangeCallback: (object: RecordOfObjectEntity) => void,
): RecordOfObjectsState {
  if (!objects) {
    return state;
  }
  let objectsState = state.get('byId');

  if (objects) {
    Object.values(objects).forEach((backendObject) => {
      const transformed = transformObject(backendObject);

      const prevObject = objectsState.get(transformed.id);

      if (prevObject) {
        if (transformed.name !== undefined) {
          objectsState = updateField(objectsState, transformed.id, 'name', transformed.name);
        }
        if (transformed.icon !== undefined) {
          objectsState = updateField(objectsState, transformed.id, 'icon', transformed.icon);
        }

        objectChangeCallback(prevObject);
      }
      else {
        objectsState = objectsState.set(transformed.id, createObjectEntity(transformed));
      }
    });
  }

  return state.set('byId', objectsState);
}

export function addMatchIdToObject(
  state: RecordOfObjectsState,
  objectId: number,
  matchId: number,
): RecordOfObjectsState {
  const matchIds = state.get('byId')?.get(objectId)?.get('matchIds');

  if (!matchIds) {
    console.error('lib > betting-entities > reducers > objects > addMatchIdToObject: `matchIds` not found');
    return state;
  }
  matchIds.add(matchId);
  return state.setIn(['byId', objectId, 'matchIds'], new Set(matchIds));
}

export function removeMatchIdFromObjects(
  state: RecordOfObjectsState,
  objectId: number,
  matchId: number,
  removeObjectsWithEmptyMatchIds = true,
): RecordOfObjectsState {
  let byId = state.get('byId');
  if (!byId.has(objectId)) {
    return state;
  }

  const matchIds = byId.get(objectId)?.get('matchIds');
  matchIds?.delete(matchId);
  byId = byId.setIn([objectId, 'matchIds'], new Set(matchIds));

  if (!removeObjectsWithEmptyMatchIds) {
    return state.set('byId', byId);
  }

  const object = byId.get(objectId);

  if (object?.matchIds.size === 0) {
    byId = byId.remove(objectId);
  }

  return state.set('byId', byId);
}

export function clearEmptyObjects(state: RecordOfObjectsState): RecordOfObjectsState {
  return state.update('byId', (byId) => (
    byId.filter((object) => object.matchIds.size > 0)
  ));
}
